/**
 * Project entity DTOs.
 *
 * Mirrors the Prisma `Project` model (prisma/schema.prisma:234). Ids use the
 * shared `entityIdSchema` (non-empty opaque string) — real rows are uuid()
 * per the Prisma default, but demo/seed tenants mint readable non-UUID ids,
 * so the wire schema must not hard-require uuid shape. Date columns surface
 * as ISO-8601 strings on the wire (NOT z.date() — JSON has no Date primitive).
 *
 * Optional vs nullable: a field is marked `.nullable()` here when the
 * Prisma column is explicitly nullable (`String?` / `Int?` / `DateTime?`).
 * Required string columns stay required.
 */
import { z } from "zod";
import { INT32_MAX, INT32_MIN, countSchema, entityIdSchema } from "../common";
import { projectInductionTextField } from "../project-induction-limits";

// Shared optimistic-write contract. Address-bound safety facts retain their
// signed siteSafetyContext protocol rather than independent text preconditions.
export const PRECONDITION_TEXT_FIELDS = [
  "title",
  "reference",
  "authorName",
  "worksSupervisorName",
  "worksSupervisorPhone",
  "siteDirections",
  "publicProtectionNotes",
  "keyRisks",
  "scopeDescription",
  "siteIntelligenceSummary",
  "estimatedDuration",
  "clientName",
  "clientAddress",
  "clientContactName",
  "clientContactPhone",
  "clientContactEmail",
  "principalDesigner",
  "principalDesignerPhone",
  "principalDesignerEmail",
  "principalDesignerCompany",
  "principalContractor",
  "principalContractorPhone",
  "principalContractorEmail",
  "principalContractorCompany",
  "architectName",
  "architectCompany",
  "architectPhone",
  "architectEmail",
  "structuralEngineer",
  "structuralEngineerCompany",
  "structuralEngineerPhone",
  "structuralEngineerEmail",
  "cdmCoordinator",
  "siteRules",
  "welfareArrangements",
  "fireStrategy",
  "siteAccessArrangements",
  "deliveryArrangements",
  "siteHoarding",
  "safetyArrangements",
  "trainingRequirements",
  "emergencyProcedures",
  "firstAidArrangements",
  "emergencyScenarios",
] as const;
export const PRECONDITION_DATE_FIELDS = ["startDate", "endDate"] as const;
export function isCalendarDay(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}
const expectedText = z.string().nullable().optional();
const expectedDay = z.string().refine(value => value === "" || isCalendarDay(value), {
  message: "Expected a YYYY-MM-DD date",
}).nullable().optional();
export const ProjectExpectedValuesSchema = z.object({
  ...Object.fromEntries(PRECONDITION_TEXT_FIELDS.map(field => [field, expectedText])) as
    Record<(typeof PRECONDITION_TEXT_FIELDS)[number], typeof expectedText>,
  ...Object.fromEntries(PRECONDITION_DATE_FIELDS.map(field => [field, expectedDay])) as
    Record<(typeof PRECONDITION_DATE_FIELDS)[number], typeof expectedDay>,
}).strict();
export type ProjectExpectedValues = z.infer<typeof ProjectExpectedValuesSchema>;

export const PROJECT_STATUSES = ["ACTIVE", "ARCHIVED", "PROCESSING"] as const;

export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export const ProjectStatusSchema = z.enum(PROJECT_STATUSES);

/**
 * Confirmation copy for the one destructive side effect of project archive:
 * open Plant assignment periods are closed on the archive date, never deleted.
 * Shared by web, native and the API conflict envelope so those surfaces cannot
 * describe different consequences.
 */
export const PROJECT_ARCHIVE_PLANT_CONFIRMATION =
  "This project still has plant recorded on site. Remove it before archiving, or confirm that the assignment history should be closed on the archive date.";

export const PROJECT_ARCHIVE_PLANT_CONFIRMATION_CODE =
  "PLANT_ASSIGNMENT_CLOSURE_CONFIRMATION_REQUIRED" as const;

export const CONTRACTOR_ROLES = [
  "PRINCIPAL_CONTRACTOR",
  "SUBCONTRACTOR",
  "ONLY_CONTRACTOR",
] as const;

export type ContractorRole = (typeof CONTRACTOR_ROLES)[number];

export const ContractorRoleSchema = z.enum(CONTRACTOR_ROLES);

/**
 * Optional person-on-project entry stored in Project.projectTeamJson.
 * Kept loose-but-validated so the field can evolve without a contracts bump.
 */
export const ProjectTeamMember = z.object({
  name: z.string(),
  role: z.string(),
  company: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().optional(),
});

// #2411 server-boundary variant.
export const ProjectTeamMemberStrict = ProjectTeamMember.strict();

export type ProjectTeamMember = z.infer<typeof ProjectTeamMember>;

/**
 * Full Project DTO returned by GET /api/mobile/v1/projects/:id. Mirrors the
 * full Prisma row minus relations (relations come back via their own
 * endpoints — keeps payloads small and pagination clean).
 */
export const ProjectSchema = z
  .object({
    id: entityIdSchema,
    userId: z.string(),
    title: z.string(),
    siteAddress: z.string(),
    scopeDescription: z.string().nullable(),
    domesticClient: z.boolean().nullable(),
    setupServicePurchased: z.boolean(),
    estimatedDuration: z.string().nullable(),
    // Column mirror, not client input: bound by the int4 range the column can
    // hold, never by a product cap, so no legacy row fails to parse.
    contractorCount: z.number().int().min(INT32_MIN).max(INT32_MAX).nullable(),
    sourceDocumentUrl: z.string().nullable(),
    clientName: z.string().nullable(),
    startDate: z.string().datetime().nullable(),
    siteIntelligenceSummary: z.string().nullable(),
    siteRules: z.string().nullable(),
    welfareArrangements: z.string().nullable(),
    fireStrategy: z.string().nullable(),
    suggestedHazardSlugs: z.array(z.string()),
    principalDesigner: z.string().nullable(),
    principalDesignerPhone: z.string().nullable(),
    principalDesignerEmail: z.string().nullable(),
    principalDesignerCompany: z.string().nullable(),
    clientContactPhone: z.string().nullable(),
    clientContactEmail: z.string().nullable(),
    clientContactName: z.string().nullable(),
    clientAddress: z.string().nullable(),
    reference: z.string().nullable(),
    endDate: z.string().datetime().nullable(),
    authorName: z.string().nullable(),
    worksSupervisorName: z.string().nullable(),
    worksSupervisorPhone: z.string().nullable(),
    siteDirections: z.string().nullable(),
    assemblyPoint: z.string().nullable(),
    publicProtectionNotes: z.string().nullable(),
    siteAccessArrangements: z.string().nullable(),
    deliveryArrangements: z.string().nullable(),
    principalContractor: z.string().nullable(),
    principalContractorPhone: z.string().nullable(),
    principalContractorEmail: z.string().nullable(),
    principalContractorCompany: z.string().nullable(),
    architectName: z.string().nullable(),
    architectCompany: z.string().nullable(),
    architectPhone: z.string().nullable(),
    architectEmail: z.string().nullable(),
    structuralEngineer: z.string().nullable(),
    structuralEngineerCompany: z.string().nullable(),
    structuralEngineerPhone: z.string().nullable(),
    structuralEngineerEmail: z.string().nullable(),
    cdmCoordinator: z.string().nullable(),
    projectTeam: z.array(ProjectTeamMember).nullable(),
    keyRisks: z.string().nullable(),
    siteHoarding: z.string().nullable(),
    safetyArrangements: z.string().nullable(),
    trainingRequirements: z.string().nullable(),
    emergencyProcedures: z.string().nullable(),
    // The remaining two of the 11 single-sourced safety fields the Site Safety
    // Completeness Lock checks (lib/projects/site-safety-completeness.ts). Added
    // for the mobile parity work (#935) so the native app can read them back to
    // compute completeness + prefill the Site Safety Setup flow. Older mobile
    // clients tolerate the two extra keys via `safeParseOrWarn`.
    emergencyScenarios: z.string().nullable(),
    utilityIsolation: z.string().nullable(),
    nearestHospital: z.string().nullable(),
    firstAidArrangements: z.string().nullable(),
    contractorRole: ContractorRoleSchema,
    status: ProjectStatusSchema,
    isTemplate: z.boolean(),
    templateId: entityIdSchema.nullable(),
    f10NotifiedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    // Opaque HMAC returned by owner-detail GET. Optional for compatibility
    // with list/create responses, which do not mint an edit context.
    siteSafetyContext: z.string().optional(),
  });

export type Project = z.infer<typeof ProjectSchema>;

// #2411 server-boundary variant — full depth, including the projectTeam
// element the mobile app parses permissively.
export const ProjectStrict = ProjectSchema.extend({
  projectTeam: z.array(ProjectTeamMemberStrict).nullable(),
}).strict();

/**
 * Compact project entry used in list views. Drops the long-form text fields
 * so paginated responses stay under a few KB per page.
 */
export const ProjectImportStateSchema = z.enum(["RUNNING", "PAUSED", "FAILED"]);
export const ProjectImportStatusSchema = z.object({
  state: ProjectImportStateSchema,
  canResume: z.boolean(),
});
export type ProjectImportStatus = z.infer<typeof ProjectImportStatusSchema>;
export const ProjectImportResumeResponseSchema = z.object({ outcome: z.enum(["running", "failed", "complete"]) });
export type ProjectImportResumeResponse = z.infer<typeof ProjectImportResumeResponseSchema>;

export const ProjectSummarySchema = z
  .object({
    importState: ProjectImportStateSchema.optional(),
    id: entityIdSchema,
    title: z.string(),
    siteAddress: z.string(),
    status: ProjectStatusSchema,
    contractorRole: ContractorRoleSchema,
    startDate: z.string().datetime().nullable(),
    endDate: z.string().datetime().nullable(),
    f10NotifiedAt: z.string().datetime().nullable(),
    setupServicePurchased: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  });

export type ProjectSummary = z.infer<typeof ProjectSummarySchema>;

// #2411 server-boundary variant.
export const ProjectSummaryStrict = ProjectSummarySchema.strict();

/**
 * Identity-only project entry for a field-only Site Supervisor. Deliberately
 * excludes contacts, duty holders, source files, safety setup and ownership.
 */
export const SupervisorProjectSchema = z.object({
  id: entityIdSchema,
  title: z.string(),
  siteAddress: z.string(),
  status: ProjectStatusSchema,
});

export type SupervisorProject = z.infer<typeof SupervisorProjectSchema>;

// #2411 server-boundary variants: the mobile app parses the permissive bases;
// the supervisor routes serialise through the Strict forms.
export const SupervisorProjectStrict = SupervisorProjectSchema.strict();

export const SupervisorProjectResponseStrict = z
  .object({ project: SupervisorProjectStrict })
  .strict();

export const SupervisorProjectResponse = z
  .object({ project: SupervisorProjectSchema })
  .strict();

export type SupervisorProjectResponse = z.infer<
  typeof SupervisorProjectResponse
>;

/**
 * POST /api/mobile/v1/projects body.
 *
 * Required fields match the web-side `createProjectSchema` in
 * lib/validators.ts so behaviour stays identical across surfaces.
 */
export const ProjectCreateRequest = z
  .object({
    title: projectInductionTextField("title").pipe(z.string().min(3)),
    siteAddress: projectInductionTextField("siteAddress").pipe(z.string().min(5)),
    /// The SavedSite this job is at (site grants cover future projects, so a
    /// job created from a saved site MUST carry the link or supervisors
    /// assigned to that site never see it). Tenant-validated server-side.
    /// "" normalises to null: an empty select is "no link", never an FK value.
    savedSiteId: z
      .string()
      .nullable()
      .optional()
      .transform((v) => (v === "" ? null : v)),
    scopeDescription: z.string().nullable().optional(),
    domesticClient: z.boolean().nullable().optional(),
    estimatedDuration: z.string().nullable().optional(),
    contractorCount: countSchema.nullable().optional(),
    contractorRole: ContractorRoleSchema.optional(),
    clientName: z.string().nullable().optional(),
    startDate: z.string().datetime().nullable().optional(),
    endDate: z.string().datetime().nullable().optional(),
    reference: z.string().nullable().optional(),
    siteRules: projectInductionTextField("siteRules").nullable().optional(),
    welfareArrangements: projectInductionTextField("welfareArrangements").nullable().optional(),
    fireStrategy: projectInductionTextField("fireStrategy").nullable().optional(),
    siteAccessArrangements: z.string().nullable().optional(),
    deliveryArrangements: z.string().nullable().optional(),
    publicProtectionNotes: z.string().nullable().optional(),
    assemblyPoint: projectInductionTextField("assemblyPoint").nullable().optional(),
    nearestHospital: projectInductionTextField("nearestHospital").nullable().optional(),
    firstAidArrangements: projectInductionTextField("firstAidArrangements").nullable().optional(),
    emergencyProcedures: projectInductionTextField("emergencyProcedures").nullable().optional(),
    keyRisks: z.string().nullable().optional(),
    siteHoarding: z.string().nullable().optional(),
    safetyArrangements: z.string().nullable().optional(),
    trainingRequirements: z.string().nullable().optional(),
    // Two of the 11 mandatory safety fields the completeness lock checks. The
    // mobile Site Safety Setup flow (#935) PATCHes the full 11-field set, so the
    // update body MUST accept these or the strict parse would 400. Flow into
    // ProjectUpdateRequest via `.partial()` below.
    emergencyScenarios: z.string().nullable().optional(),
    utilityIsolation: z.string().nullable().optional(),
    principalContractor: projectInductionTextField("principalContractor").nullable().optional(),
    principalContractorPhone: projectInductionTextField("principalContractorPhone").nullable().optional(),
    principalContractorEmail: projectInductionTextField("principalContractorEmail").nullable().optional(),
    principalContractorCompany: projectInductionTextField("principalContractorCompany").nullable().optional(),
    principalDesigner: z.string().nullable().optional(),
    principalDesignerPhone: z.string().nullable().optional(),
    principalDesignerEmail: z.string().nullable().optional(),
    principalDesignerCompany: z.string().nullable().optional(),
    clientContactName: z.string().nullable().optional(),
    clientContactPhone: z.string().nullable().optional(),
    clientContactEmail: z.string().nullable().optional(),
    clientAddress: z.string().nullable().optional(),
    architectName: z.string().nullable().optional(),
    architectCompany: z.string().nullable().optional(),
    architectPhone: z.string().nullable().optional(),
    architectEmail: z.string().nullable().optional(),
    structuralEngineer: z.string().nullable().optional(),
    structuralEngineerCompany: z.string().nullable().optional(),
    structuralEngineerPhone: z.string().nullable().optional(),
    structuralEngineerEmail: z.string().nullable().optional(),
    cdmCoordinator: z.string().nullable().optional(),
    authorName: z.string().nullable().optional(),
    worksSupervisorName: z.string().nullable().optional(),
    worksSupervisorPhone: z.string().nullable().optional(),
    siteDirections: z.string().nullable().optional(),
    siteIntelligenceSummary: z.string().nullable().optional(),
  })
  .strict();

export type ProjectCreateRequest = z.infer<typeof ProjectCreateRequest>;

/**
 * PATCH /api/mobile/v1/projects/:id body. Every field is optional; `status`
 * is added so the mobile archive flow can flip a project to ARCHIVED.
 *
 * `f10NotifiedAt` is gated server-side as a Pro feature (see web route at
 * app/api/projects/[projectId]/route.ts) — sending it on a starter plan
 * returns PLAN_LOCKED.
 */
export const ProjectUpdateRequest = ProjectCreateRequest.partial()
  .extend({
    status: z.enum(["ACTIVE", "ARCHIVED"]).optional(),
    f10NotifiedAt: z.string().datetime().nullable().optional(),
    expectedValues: ProjectExpectedValuesSchema.optional(),
    confirmPlantAssignmentClosure: z.boolean().optional(),
    siteSafetyContext: z.string().max(256).optional(),
    nearestHospitalProof: z.string().max(256).optional(),
  })
  .strict();

export type ProjectUpdateRequest = z.infer<typeof ProjectUpdateRequest>;

/** Dedicated status-route body; PROCESSING remains server-owned. */
export const ProjectStatusUpdateRequest = z
  .object({
    status: z.enum(["ACTIVE", "ARCHIVED"]),
    confirmPlantAssignmentClosure: z.boolean().optional(),
  })
  .strict();

export type ProjectStatusUpdateRequest = z.infer<
  typeof ProjectStatusUpdateRequest
>;

/**
 * POST /api/mobile/v1/projects/:id/clone body. Optional override title for
 * the clone — defaults to "<original> (copy)" server-side.
 */
export const ProjectCloneRequest = z
  .object({
    title: projectInductionTextField("title").pipe(z.string().min(3)).optional(),
  })
  .strict();

export type ProjectCloneRequest = z.infer<typeof ProjectCloneRequest>;

export const ProjectResponse = z.object({ project: ProjectSchema }).strict();
export type ProjectResponse = z.infer<typeof ProjectResponse>;
